import React, { FC, memo } from 'react';
import { ThemeProvider } from 'styled-components';
import { overrideTheme } from '@clds/component-theme';
import { t as codeSnippetTheme } from '@cld/code-snippet-next';
import { SdkCodeSnippetsPure } from './SdkCodeSnippetsPure';
import { useActiveSnippetChange, useSelectedSnippet, useSnippetState } from './hooks';
import { SdkCodeSnippetsLayout } from './components/SdkCodeSnippetsLayout';
import { SdkCodeSnippetsPureProps } from './types';
import { getThemeConfiguration } from './utils/getThemeConfiguration';
import { OpenInTransformationBuilderButton, shouldOpenTransformationBuilder } from './components/OpenInTransformationBuilderButton';

/**
 * get external snippets data, can be used for ssr
 */
export const SdkCodeSnippetsUncontrolled: FC<React.PropsWithChildren<SdkCodeSnippetsPureProps>> = memo(
  ({
    snippets,
    withBackground = false,
    onSnippetChange,
    onTabChange,
    onVersionChange,
    onCopy,
    parsedUrl,
    height,
    fontSize,
    lineHeight,
    headerSize,
    extraHeaderButtons,
    openInBuilderButtonConfig,
    ...rest
  }) => {
    const { language, setLanguage, version, setVersion } = useSnippetState(snippets);
    const activeSnippet = useSelectedSnippet(snippets, language, version);
    const onCopySnippet = () => onCopy?.(activeSnippet);
    const onChangeTab = () => onTabChange?.(language);
    const onLangVersionChange = (version?: string) => {
      setVersion(version);
      onVersionChange?.(version);
    };

    useActiveSnippetChange(activeSnippet, onSnippetChange, onChangeTab, language, version);

    return (
      <ThemeProvider
        // eslint-disable-next-line
        theme={(parent: object) => ({
          ...parent,
          components: overrideTheme(codeSnippetTheme, { code: getThemeConfiguration({ fontSize, lineHeight }) }),
        })}
      >
        <SdkCodeSnippetsLayout withBackground={withBackground} {...rest}>
          <SdkCodeSnippetsPure
            snippets={snippets}
            activeLanguage={language}
            onLanguageChange={setLanguage}
            langVersion={version}
            onLangVersionChange={onLangVersionChange}
            onCopy={onCopySnippet}
            parsedUrl={parsedUrl}
            height={height}
            headerSize={headerSize}
            extraHeaderButtons={extraHeaderButtons}
            footer={
              openInBuilderButtonConfig?.enabled && shouldOpenTransformationBuilder(parsedUrl) ? (
                <OpenInTransformationBuilderButton host={openInBuilderButtonConfig.host} target={openInBuilderButtonConfig.target} parsedUrl={parsedUrl} />
              ) : undefined
            }
          />
        </SdkCodeSnippetsLayout>
      </ThemeProvider>
    );
  }
);
